import {noop} from '@benchling/js-utils';
import {Tag, TagColor, TagSize} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {getPresentationalDocumentLayoutConfiguration} from 'analysis-tool/outputs/ConfigureDocumentUtil';
import {PresentationalOutputDocumentLayout} from 'analysis-tool/outputs/OutputDocumentLayout';
import {getOutputDataFrameName} from 'analysis-tool/outputs/OutputsUtil';
import AnalysisTemplateFlowchartView from 'analysis-tool/templates/AnalysisTemplateFlowchartView';
import AnalysisClientStateIdContext from 'analysis-tool/ui/AnalysisClientStateIdContext';
import SelectableCard from 'analysis-tool/ui/SelectableCard';
import StepConfigurationViewTemplate from 'analysis-tool/ui/view-templates/StepConfigurationViewTemplate';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import TextArea from 'ui-library/bs/TextArea';
import * as Colors from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextInput from 'ui-library/inputs/TextInput';
import {StepObject, StepperV2, stepperStyles} from 'ui-library/stepper/Stepper';

import AnalysisTemplateHeader from './AnalysisTemplateHeader';
import {AnalysisTemplateGraphQL} from './AnalysisTemplateTypes';
import {InputTableType, getInputTableApplyStates, getInputTableApplyStateName} from './AnalysisTemplateUtil';
import {GetAnalysisTemplate, GetAnalysisTemplateVariables} from './graphql/__generated/GetAnalysisTemplate';
import {GET_ANALYSIS_TEMPLATE_QUERY} from './graphql/GetAnalysisTemplate';
import StartingTablesInfo from './StartingTablesInfo';

export interface AnalysisTemplatePageProps {
  analysisTemplate: AnalysisTemplateGraphQL;
}

export function AnalysisTemplatePage({analysisTemplate}: AnalysisTemplatePageProps): JSX.Element {
  const stepObjects: Array<StepObject> = [
    {
      key: 'template-details',
      title: <div className={css(stepperStyles.defaultStepTitle)}>Setup template details</div>,
      content: (
        <div className={classNames(css(stepperStyles.defaultStepContent), css(detailsSectionStyles.row))}>
          <div>
            <FieldLabel label="Template name" />
            <TextInput
              disabled={true}
              value={analysisTemplate.name}
              dataTestAttrs={{
                component: 'AnalysisTemplatePage',
                element: 'templateNameField',
              }}
            />
          </div>
          <div>
            <FieldLabel label="Template collection" />
            <TextInput
              disabled={true}
              value={analysisTemplate.templateCollection.name}
              dataTestAttrs={{
                component: 'AnalysisTemplatePage',
                element: 'templateCollectionField',
              }}
            />
          </div>
        </div>
      ),
    },
    {
      key: 'starting-tables',
      title: <div className={css(stepperStyles.defaultStepTitle)}>Starting tables</div>,
      content: (
        <div className={css(stepperStyles.defaultStepContent)}>
          <div className={css(inputTablesSectionStyles.header)}>Starting Tables</div>
          <div className={css(inputTablesSectionStyles.helpText)}>
            These tables will be created when this template is used.
            <StartingTablesInfo />
          </div>
          <div className={css(inputTablesSectionStyles.wrapper)}>
            {getInputTableApplyStates(analysisTemplate, analysisTemplate.dataFrameVariables).map(
              (inputTable, index) => (
                <SelectableCard
                  key={index}
                  selected={true}
                  onChange={noop}
                  title={
                    <div className={css(inputTablesSectionStyles.cardTitle)}>
                      <BenchlingIcon className={css(inputTablesSectionStyles.cardIcon)} icon="table" />
                      {getInputTableApplyStateName(inputTable)}
                      <div
                        {...getTestProps({
                          component: 'AnalysisTemplatePage',
                          element: 'inputTableTypeTag',
                          index: String(index),
                        })}
                      >
                        <Tag
                          text={inputTable.inputType}
                          color={
                            inputTable.inputType === InputTableType.VARIABLE
                              ? TagColor.PURPLE
                              : TagColor.SECONDARY
                          }
                          size={TagSize.SMALL}
                        />
                      </div>
                    </div>
                  }
                >
                  {inputTable.inputType === InputTableType.VARIABLE && inputTable.description && (
                    <TextArea disabled={true} value={inputTable.description} style={{resize: 'none'}} />
                  )}
                </SelectableCard>
              )
            )}
          </div>
        </div>
      ),
    },
  ];

  const exportDatasetSteps = analysisTemplate.execution.steps.filter(
    (step) => step.definition.stepType === PipelineStepType.EXPORT_DATA_CATALOG_DATASET
  );
  if (exportDatasetSteps.length > 0) {
    stepObjects.push({
      key: 'created-datasets',
      title: <div className={css(stepperStyles.defaultStepTitle)}>Created datasets</div>,
      content: (
        <div className={css(stepperStyles.defaultStepContent)}>
          <div className={css(styles.description)}>
            These datasets will be saved in the same folder as the source document when the template is run
            from a worksheet.
          </div>
          <div
            className={css(styles.datasetOutputList)}
            {...getTestProps({component: 'AnalysisTemplatePage', element: 'createdDatasetsList'})}
          >
            {exportDatasetSteps.map((exportDatasetStep, index) => (
              <span key={index}>
                <BenchlingIcon icon="table" className="u-mini-spacing-left" />
                <span className="u-mini-spacing-left">
                  {getOutputDataFrameName(analysisTemplate, exportDatasetStep, [])}
                </span>
              </span>
            ))}
          </div>
        </div>
      ),
    });
  }

  const documentLayoutConfiguration = getPresentationalDocumentLayoutConfiguration(analysisTemplate);
  if (documentLayoutConfiguration != null) {
    stepObjects.push({
      key: 'outputs',
      title: <div className={css(stepperStyles.defaultStepTitle)}>Outputs</div>,
      content: (
        <div className={css(stepperStyles.defaultStepContent)}>
          <div className={css(styles.description)}>
            If run from a worksheet, these sections will be added to a new step.
          </div>
          <PresentationalOutputDocumentLayout
            documentLayoutConfiguration={documentLayoutConfiguration}
            onAddSection={noop}
            onUpdateSectionTitle={noop}
            onDeleteSection={noop}
            onMoveItem={noop}
            readOnly={true}
          />
        </div>
      ),
    });
  }

  return (
    <>
      <AnalysisTemplateHeader analysisTemplate={analysisTemplate} />
      <StepConfigurationViewTemplate
        clientStateId={analysisTemplate.id}
        dataFrames={[]}
        disableFinalizeButton={true}
        finalizeButtonText=""
        messages={[]}
        onCancel={null}
        onFinalize={null}
        renderAdditionalPreviewContent={() => (
          <AnalysisTemplateFlowchartView analysisTemplate={analysisTemplate} />
        )}
        title="Template configuration"
      >
        <StepperV2 steps={stepObjects} />
      </StepConfigurationViewTemplate>
    </>
  );
}

export interface QueryingAnalysisTemplatePageProps {
  analysisTemplateId: string;
}

export default function QueryingAnalysisTemplatePage({
  analysisTemplateId,
}: QueryingAnalysisTemplatePageProps): JSX.Element | null {
  return (
    <AnalysisClientStateIdContext.Provider value={analysisTemplateId}>
      <div
        {...getTestProps({component: 'AnalysisTemplatePage', element: 'root'})}
        className={css(styles.wrapper)}
      >
        <BenchlingQuery<GetAnalysisTemplate, GetAnalysisTemplateVariables>
          query={GET_ANALYSIS_TEMPLATE_QUERY}
          renderLoading={() => <CenteredHorizontalThrobber />}
          variables={{
            analysisTemplateId,
          }}
        >
          {(data) =>
            isInaccessible(data.analysisTemplate) ? (
              <WorkspaceItemInaccessibleGuard
                inaccessibleInfo={data.analysisTemplate}
                modelType={ModelTypeConstants.ANALYSIS_TEMPLATE}
              />
            ) : (
              <AnalysisTemplatePage analysisTemplate={data.analysisTemplate} />
            )
          }
        </BenchlingQuery>
      </div>
    </AnalysisClientStateIdContext.Provider>
  );
}

const GRID_WITH_TWO_COLUMNS = {display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)'} as const;
const styles = StyleSheet.create({
  datasetOutputList: {display: 'flex', flexDirection: 'column', gap: 6, ...FONT_PROXIMA_13, marginTop: 20},
  description: {marginBottom: 8, ...FONT_PROXIMA_13},
  wrapper: {
    height: '100%',
    width: '100%',
  },
});
const detailsSectionStyles = StyleSheet.create({row: {...GRID_WITH_TWO_COLUMNS, gap: 16}});
const inputTablesSectionStyles = StyleSheet.create({
  cardIcon: {color: Colors.BLUE},
  cardTitle: {display: 'flex', gap: 8, alignItems: 'center'},
  header: {
    textTransform: 'uppercase',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: Colors.GRAY_DARKER,
  },
  helpText: {paddingBottom: 8, paddingTop: 8},
  wrapper: {...GRID_WITH_TWO_COLUMNS, gap: 8},
});
